import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Camera, CheckCircle2, Clock, Loader2, LogIn, LogOut, MapPin, Phone, ScanLine, X, Zap, Calculator, FileSignature, Paperclip, MessageCircle, ExternalLink, CalendarClock } from 'lucide-react';
import { Modalidade, isRemota, modalidadeLabel, normalizarModalidade } from '../../../src/lib/modalidade';
import { PosVendaResultado, POS_VENDA_RESULTADO_LABELS } from '../../../types';

export interface OcrCandidato {
    numero: string;
    score: number;
}

export interface VisitaParadaInfo {
    id: string;
    titulo: string;
    checkin_em?: string | null;
    cidade?: string | null;
    bairro?: string | null;
    segmento?: string | null;
    telefone?: string | null;
    kwh?: number | null;
    modalidade?: Modalidade;
    negociacaoId?: number | null;
    /** `tarefas.tipo` da visita. 'pos_venda' habilita os chips de resultado na conclusão. */
    tipo?: string | null;
    // Leitura do medidor (modo foto): resultado do OCR + número já cadastrado no lead
    ocrStatus?: string | null;              // processando | concluido | falhou
    ocrCandidatos?: OcrCandidato[] | null;
    numeroMedidorLead?: string | null;      // já vinculado ao lead em visita anterior
    fotoUrl?: string | null;                // foto do medidor capturada no check-in
}

export interface VisitaConclusao {
    descricao: string;
    statusVisita?: string | null;
    proximaAcao?: string | null;
    // Loop "concluir → planejar próxima" (doc 03 §4)
    proximaVisitaData?: string | null;
    proximaVisitaModalidade?: Modalidade;
    /** Número do medidor confirmado pelo operador (modo foto). */
    numeroMedidor?: string | null;
    /**
     * Visita de pós-venda: chips opcionais de resultado, para o relatório do gestor
     * não depender de ler texto livre. Nunca bloqueiam a conclusão.
     */
    resultadoCategoria?: PosVendaResultado[] | null;
}

/** Vendedor: registro rápido sem foto. Motoboy: leitura de medidor com foto obrigatória. */
export type VisitaModo = 'comercial' | 'foto';

export const STATUS_VISITA_OPTS: Array<{ value: string; label: string }> = [
    { value: 'visita_realizada', label: 'Visita Realizada' },
    { value: 'nao_encontrado', label: 'Não Encontrado' },
    { value: 'empresa_fechada', label: 'Empresa Fechada' },
    { value: 'sem_interesse', label: 'Sem Interesse' },
    { value: 'retornar_depois', label: 'Retornar Depois' },
    { value: 'nova_reuniao', label: 'Agendou Nova Reunião' },
    { value: 'convertido', label: 'Convertido em Oportunidade' },
];

// Próxima ação reflete a modalidade da visita (doc Tarefas §2.3 / doc 03)
const PROXIMA_ACAO_COMUM = [{ value: '', label: 'Sem ação necessária' }];
export const PROXIMA_ACAO_PRESENCIAL = [
    ...PROXIMA_ACAO_COMUM,
    { value: 'criar_simulacao', label: 'Criar simulação' },
    { value: 'gerar_termo', label: 'Gerar termo' },
    { value: 'agendar_retorno', label: 'Agendar retorno (presencial)' },
];
export const PROXIMA_ACAO_TELEFONE = [
    ...PROXIMA_ACAO_COMUM,
    { value: 'agendar_visita_presencial', label: 'Agendar visita presencial' },
    { value: 'agendar_nova_ligacao', label: 'Agendar nova ligação' },
    { value: 'criar_simulacao', label: 'Criar simulação' },
];
export const proximaAcaoOpts = (modalidade?: Modalidade) =>
    isRemota(modalidade) ? PROXIMA_ACAO_TELEFONE : PROXIMA_ACAO_PRESENCIAL;

// Ações de próxima etapa que geram automaticamente a próxima visita/ligação na agenda
export const PROXIMA_ACAO_AGENDA: Record<string, Modalidade> = {
    agendar_visita_presencial: 'presencial',
    agendar_retorno: 'presencial',
    agendar_nova_ligacao: 'remota',
};

const PROXIMA_ACAO_LABELS: Record<string, string> = {
    '': 'Sem ação necessária',
    criar_simulacao: 'Criar simulação',
    gerar_termo: 'Gerar termo',
    agendar_retorno: 'Agendar retorno (presencial)',
    agendar_visita_presencial: 'Agendar visita presencial',
    agendar_nova_ligacao: 'Agendar nova ligação',
    // valores legados
    retornar_ligacao: 'Retornar ligação',
    agendar_reuniao: 'Agendar reunião',
    enviar_proposta: 'Enviar proposta',
    enviar_simulacao: 'Enviar simulação',
};

export const statusVisitaLabel = (v?: string | null) =>
    STATUS_VISITA_OPTS.find(o => o.value === v)?.label || v || '';

export const proximaAcaoLabel = (v?: string | null) =>
    PROXIMA_ACAO_LABELS[v || ''] || v || '';

interface VisitaModalProps {
    parada: VisitaParadaInfo | null;
    etapa: 'iniciar' | 'encerrar';
    modo: VisitaModo;
    onClose: () => void;
    onIniciar: (parada: VisitaParadaInfo, foto: File) => Promise<void>;
    onEncerrar: (parada: VisitaParadaInfo, dados: VisitaConclusao) => Promise<void>;
    /** Modo foto: refaz a foto do medidor durante a conferência (re-upload + novo OCR). */
    onRefazerFoto?: (parada: VisitaParadaInfo, foto: File) => Promise<void>;
    /** Abre o detalhe completo do lead (doc 03 §3.1/§3.2). */
    onAbrirLead?: (negociacaoId: number) => void;
    /** Permite alterar o tipo (Presencial/Telefone) de uma visita já aberta. Se ausente, o selo é só leitura. */
    onChangeModalidade?: (modalidade: Modalidade) => void | Promise<void>;
}

/** Status de visita que disparam o agendamento da próxima (doc 03 §4). */
const STATUS_AGENDA_PROXIMA = ['retornar_depois', 'nova_reuniao'];
const addDiasIso = (n: number) => { const d = new Date(); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

/**
 * Check-in / Check-out de visita (Rotas v2).
 * modo 'comercial' (vendedor): concluir com Status da Visita + observação obrigatórios — sem foto.
 * modo 'foto' (motoboy/leitura): check-in exige foto do medidor; observação opcional no check-out.
 */
const VisitaModal: React.FC<VisitaModalProps> = ({ parada, etapa, modo, onClose, onIniciar, onEncerrar, onAbrirLead, onChangeModalidade, onRefazerFoto }) => {
    const [foto, setFoto] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);
    const [descricao, setDescricao] = useState('');
    const [statusVisita, setStatusVisita] = useState('visita_realizada');
    const [proximaAcao, setProximaAcao] = useState('');
    const [proxVisitaData, setProxVisitaData] = useState(addDiasIso(1));
    const [proxVisitaModalidade, setProxVisitaModalidade] = useState<Modalidade>('presencial');
    const [modalidade, setModalidade] = useState<Modalidade>('presencial');
    const [salvando, setSalvando] = useState(false);
    const [numeroMedidor, setNumeroMedidor] = useState('');
    const [resultadoCategoria, setResultadoCategoria] = useState<PosVendaResultado[]>([]);
    const [refazendoFoto, setRefazendoFoto] = useState(false);
    const medidorTocado = useRef(false); // operador editou manualmente → OCR não sobrescreve
    const fileRef = useRef<HTMLInputElement>(null);
    const refazerRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Reset ao abrir para outra parada/etapa
        setFoto(null);
        setPreview(null);
        setDescricao('');
        setStatusVisita('visita_realizada');
        setProximaAcao('');
        setProxVisitaData(addDiasIso(1));
        setProxVisitaModalidade('presencial');
        setModalidade(normalizarModalidade(parada?.modalidade));
        setSalvando(false);
        setNumeroMedidor('');
        setResultadoCategoria([]);
        setRefazendoFoto(false);
        medidorTocado.current = false;
    }, [parada?.id, etapa]);

    // Preenche com o melhor candidato do OCR (ou o nº já cadastrado no lead) enquanto
    // o operador não digitou nada — o resultado pode chegar com o modal já aberto (polling do pai).
    useEffect(() => {
        if (medidorTocado.current || etapa !== 'encerrar' || modo !== 'foto') return;
        const sugestao = parada?.ocrCandidatos?.[0]?.numero || parada?.numeroMedidorLead || '';
        if (sugestao) setNumeroMedidor(sugestao);
    }, [parada?.ocrCandidatos, parada?.numeroMedidorLead, etapa, modo]);

    // Altera o tipo da visita já aberta (Presencial/Telefone) — persiste via callback do pai
    const mudarModalidade = (m: Modalidade) => {
        if (m === modalidade || !onChangeModalidade) return;
        setModalidade(m); // otimista
        Promise.resolve(onChangeModalidade(m)).catch(() => setModalidade(modalidade));
    };

    useEffect(() => {
        if (!foto) { setPreview(null); return; }
        const url = URL.createObjectURL(foto);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [foto]);

    // Próxima ação de agendamento já define a modalidade da próxima visita
    useEffect(() => {
        const m = PROXIMA_ACAO_AGENDA[proximaAcao];
        if (m) setProxVisitaModalidade(m);
    }, [proximaAcao]);

    if (!parada) return null;

    // Comercial: status + observação obrigatórios (pedido Bruno 10/jul: visitas concluídas sem
    // observação). Foto: foto obrigatória no check-in.
    const isObservacaoValida = descricao.trim().length > 0;
    const podeConfirmar = etapa === 'iniciar' ? !!foto : (modo === 'comercial' ? !!statusVisita && isObservacaoValida : true);
    // Chips de resultado só na conclusão de visita de pós-venda — opcionais por definição (spec §7).
    const mostraResultado = etapa === 'encerrar' && modo === 'comercial' && parada.tipo === 'pos_venda';
    const toggleResultado = (v: PosVendaResultado) =>
        setResultadoCategoria(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
    // Agenda a próxima visita quando o Status (Retornar Depois / Nova Reunião) ou a Próxima ação pedem (doc 03 §4 / Tarefas §2.3)
    const agendaProxima = etapa === 'encerrar' && modo === 'comercial' && (STATUS_AGENDA_PROXIMA.includes(statusVisita) || !!PROXIMA_ACAO_AGENDA[proximaAcao]);

    const confirmar = async () => {
        if (!podeConfirmar || salvando) return;
        setSalvando(true);
        try {
            if (etapa === 'iniciar') {
                await onIniciar(parada, foto!);
            } else {
                await onEncerrar(parada, {
                    descricao: descricao.trim(),
                    statusVisita: modo === 'comercial' ? statusVisita : null,
                    proximaAcao: modo === 'comercial' ? (proximaAcao || null) : null,
                    proximaVisitaData: agendaProxima ? proxVisitaData : null,
                    proximaVisitaModalidade: agendaProxima ? proxVisitaModalidade : undefined,
                    numeroMedidor: modo === 'foto' ? (numeroMedidor.replace(/\D/g, '') || null) : null,
                    resultadoCategoria: mostraResultado && resultadoCategoria.length ? resultadoCategoria : null,
                });
            }
        } finally {
            setSalvando(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center sm:p-4 bg-black/50" onClick={onClose}>
            <div
                className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md flex flex-col overflow-hidden max-h-[92vh]"
                onClick={e => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`p-2 rounded-xl shrink-0 ${etapa === 'iniciar' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'}`}>
                            {etapa === 'iniciar' ? <LogIn size={18} /> : <LogOut size={18} />}
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white">{etapa === 'iniciar' ? 'Iniciar Visita' : 'Concluir Visita'}</h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{parada.titulo}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 shrink-0">
                        <X size={18} />
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                    {/* Contexto do lead (rápido de ler na rua) */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                        {/* Selo do tipo de visita (doc 03 §1) — editável quando onChangeModalidade é fornecido */}
                        {onChangeModalidade ? (
                            <span className="flex items-center gap-0.5 p-0.5 rounded-full bg-gray-100 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600" title="Alterar tipo da visita">
                                <button
                                    type="button"
                                    onClick={() => mudarModalidade('presencial')}
                                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full font-bold transition-all ${modalidade === 'presencial' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                >
                                    <MapPin size={10} /> Presencial
                                </button>
                                <button
                                    type="button"
                                    onClick={() => mudarModalidade('remota')}
                                    className={`flex items-center gap-1 px-2 py-0.5 rounded-full font-bold transition-all ${isRemota(modalidade) ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                                >
                                    <Phone size={10} /> Remota
                                </button>
                            </span>
                        ) : (
                            <span className={`flex items-center gap-1 font-bold px-2 py-0.5 rounded-full ${isRemota(modalidade) ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'}`}>
                                {isRemota(modalidade) ? <Phone size={10} /> : <MapPin size={10} />} {modalidadeLabel(modalidade)}
                            </span>
                        )}
                        {(parada.bairro || parada.cidade) && (
                            <span className="flex items-center gap-1"><MapPin size={11} /> {[parada.bairro, parada.cidade].filter(Boolean).join(', ')}</span>
                        )}
                        {parada.segmento && <span>{parada.segmento}</span>}
                        {(parada.kwh || 0) > 0 && <span className="flex items-center gap-0.5 font-semibold text-amber-600 dark:text-amber-400"><Zap size={11} /> {Math.round(parada.kwh!).toLocaleString('pt-BR')} kWh</span>}
                        {parada.telefone && <span>{parada.telefone}</span>}
                    </div>

                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <Clock size={13} />
                        {etapa === 'iniciar'
                            ? <span>Check-in às <b>{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</b> de {new Date().toLocaleDateString('pt-BR')}</span>
                            : parada.checkin_em
                                ? <span>Check-in às <b>{new Date(parada.checkin_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</b> · concluindo às <b>{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</b></span>
                                : <span>Concluindo às <b>{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</b> de {new Date().toLocaleDateString('pt-BR')}</span>}
                    </div>

                    {etapa === 'iniciar' ? (
                        <div>
                            <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                                Foto do medidor <span className="text-red-500">* obrigatória</span>
                            </label>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={e => setFoto(e.target.files?.[0] || null)}
                            />
                            {preview ? (
                                <div className="relative">
                                    <img src={preview} alt="Foto do medidor" className="w-full max-h-64 object-cover rounded-xl border border-gray-200 dark:border-gray-700" />
                                    <button
                                        onClick={() => fileRef.current?.click()}
                                        className="absolute bottom-2 right-2 flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-full bg-black/60 text-white backdrop-blur hover:bg-black/80 transition-colors"
                                    >
                                        <Camera size={12} /> Trocar foto
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => fileRef.current?.click()}
                                    className="w-full flex flex-col items-center justify-center gap-2 py-10 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 text-gray-400 hover:border-primary hover:text-primary transition-colors"
                                >
                                    <Camera size={28} />
                                    <span className="text-xs font-semibold">Tirar foto do medidor</span>
                                </button>
                            )}
                        </div>
                    ) : (
                        <>
                            {modo === 'foto' && (
                                <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 p-3 space-y-2.5">
                                    <p className="text-xs font-bold text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
                                        <ScanLine size={13} /> Número do medidor
                                    </p>

                                    {parada.fotoUrl && (
                                        <img
                                            src={parada.fotoUrl}
                                            alt="Foto do medidor"
                                            className="w-full max-h-64 object-contain rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900"
                                        />
                                    )}

                                    {parada.numeroMedidorLead && (
                                        <p className="text-[11px] text-blue-600/80 dark:text-blue-400/80">
                                            ℹ️ Este lead já tem o medidor <b>{parada.numeroMedidorLead}</b> cadastrado.
                                        </p>
                                    )}

                                    {parada.ocrStatus === 'processando' && (
                                        <p className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                                            <Loader2 size={14} className="animate-spin" /> Lendo o número na foto...
                                        </p>
                                    )}
                                    {parada.ocrStatus === 'falhou' && (
                                        <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                            ⚠️ Não foi possível ler automaticamente — digite o número abaixo.
                                        </p>
                                    )}
                                    {parada.ocrStatus === 'concluido' && (parada.ocrCandidatos?.length || 0) === 0 && (
                                        <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                            ⚠️ Nenhum número identificado na foto — digite manualmente.
                                        </p>
                                    )}

                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        value={numeroMedidor}
                                        onChange={e => { medidorTocado.current = true; setNumeroMedidor(e.target.value.replace(/[^\d]/g, '')); }}
                                        placeholder="Ex: 12345678901"
                                        className="w-full px-3 py-2.5 text-base font-mono font-bold tracking-wider text-center rounded-xl bg-white dark:bg-gray-700/50 border border-blue-200 dark:border-blue-700 text-gray-900 dark:text-gray-100 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                                    />

                                    {(parada.ocrCandidatos?.length || 0) > 1 && (
                                        <div className="flex flex-wrap gap-1.5">
                                            <span className="text-[10px] text-gray-500 dark:text-gray-400 w-full">Outros números encontrados na foto:</span>
                                            {parada.ocrCandidatos!.filter(c => c.numero !== numeroMedidor).slice(0, 4).map(c => (
                                                <button
                                                    key={c.numero}
                                                    type="button"
                                                    onClick={() => { medidorTocado.current = true; setNumeroMedidor(c.numero); }}
                                                    className="px-2.5 py-1 rounded-full text-[11px] font-mono font-bold bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 transition-colors"
                                                >
                                                    {c.numero}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {onRefazerFoto && (
                                        <>
                                            <input
                                                ref={refazerRef}
                                                type="file"
                                                accept="image/*"
                                                capture="environment"
                                                className="hidden"
                                                onChange={async e => {
                                                    const f = e.target.files?.[0];
                                                    e.target.value = '';
                                                    if (!f) return;
                                                    setRefazendoFoto(true);
                                                    medidorTocado.current = false;
                                                    setNumeroMedidor('');
                                                    try { await onRefazerFoto(parada, f); } finally { setRefazendoFoto(false); }
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => refazerRef.current?.click()}
                                                disabled={refazendoFoto}
                                                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/40 text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 transition-all disabled:opacity-50"
                                            >
                                                {refazendoFoto ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
                                                {refazendoFoto ? 'Enviando nova foto...' : 'Refazer foto'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}

                            {modo === 'comercial' && (
                                <div>
                                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                                        Status da Visita <span className="text-red-500">*</span>
                                    </label>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {STATUS_VISITA_OPTS.map(o => (
                                            <button
                                                key={o.value}
                                                onClick={() => setStatusVisita(o.value)}
                                                className={`px-2.5 py-2 rounded-xl text-xs font-semibold border transition-all text-left ${statusVisita === o.value
                                                    ? 'bg-primary/10 border-primary/40 text-primary'
                                                    : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
                                            >
                                                {o.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                                    Observação da visita {modo === 'comercial'
                                        ? <span className="text-red-500">*</span>
                                        : <span className="text-gray-400 font-normal">(opcional)</span>}
                                </label>
                                <textarea
                                    value={descricao}
                                    onChange={e => setDescricao(e.target.value)}
                                    rows={3}
                                    placeholder={modo === 'comercial' ? 'Conversa com o proprietário, solicitou proposta...' : 'Medidor nos fundos, portão trancado...'}
                                    className="w-full px-3 py-2.5 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                                />
                                {modo === 'comercial' && !isObservacaoValida && (
                                    <p className="text-[11px] text-red-500 mt-1">Preencha a observação da visita para concluir.</p>
                                )}
                            </div>

                            {mostraResultado && (
                                <div>
                                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                                        Resultado da visita <span className="text-gray-400 font-normal">(opcional, pode marcar mais de um)</span>
                                    </label>
                                    <div className="flex flex-wrap gap-1.5">
                                        {(Object.keys(POS_VENDA_RESULTADO_LABELS) as PosVendaResultado[]).map(v => {
                                            const on = resultadoCategoria.includes(v);
                                            return (
                                                <button
                                                    key={v}
                                                    type="button"
                                                    onClick={() => toggleResultado(v)}
                                                    className={`px-2.5 py-1.5 text-[11px] font-bold rounded-full border transition-all ${
                                                        on
                                                            ? 'bg-secondary/15 border-secondary/40 text-secondary'
                                                            : 'bg-gray-50 dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-gray-300'
                                                    }`}
                                                >
                                                    {POS_VENDA_RESULTADO_LABELS[v]}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-[11px] text-gray-400 mt-1">
                                        Serve para o relatório do gestor não depender de ler o texto livre.
                                    </p>
                                </div>
                            )}

                            {modo === 'comercial' && (
                                <div>
                                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">
                                        Próxima ação <span className="text-gray-400 font-normal">(opcional)</span>
                                    </label>
                                    <select
                                        value={proximaAcao}
                                        onChange={e => setProximaAcao(e.target.value)}
                                        className="w-full text-sm px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none"
                                    >
                                        {proximaAcaoOpts(modalidade).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                            )}

                            {/* Loop "concluir → planejar próxima" (doc 03 §4): agenda a próxima visita inline */}
                            {agendaProxima && (
                                <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-2.5">
                                    <p className="text-xs font-bold text-primary flex items-center gap-1.5"><CalendarClock size={13} /> Agendar próxima visita</p>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button type="button" onClick={() => setProxVisitaModalidade('presencial')} className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${proxVisitaModalidade === 'presencial' ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-white dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                                            <MapPin size={13} /> Presencial
                                        </button>
                                        <button type="button" onClick={() => setProxVisitaModalidade('remota')} className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${isRemota(proxVisitaModalidade) ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-white dark:bg-gray-700/40 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
                                            <Phone size={13} /> Remota
                                        </button>
                                    </div>
                                    <input
                                        type="date"
                                        value={proxVisitaData}
                                        min={addDiasIso(0)}
                                        onChange={e => setProxVisitaData(e.target.value)}
                                        className="w-full px-3 py-2 text-sm rounded-xl bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    />
                                    <p className="text-[10px] text-gray-500 dark:text-gray-400">Ao concluir, a próxima visita é criada na rota de {new Date(proxVisitaData + 'T12:00:00').toLocaleDateString('pt-BR')}.</p>
                                </div>
                            )}

                            {/* Atalhos pós-visita (doc 03 §3.2) — abrem o detalhe do lead com as ações já existentes */}
                            {parada.negociacaoId && onAbrirLead && (
                                <div>
                                    <label className="text-xs font-bold text-gray-700 dark:text-gray-200 block mb-1.5">Ações pós-visita</label>
                                    <div className="grid grid-cols-2 gap-1.5">
                                        {[
                                            { icon: Calculator, label: 'Simular economia' },
                                            { icon: FileSignature, label: 'Gerar termo' },
                                            { icon: Paperclip, label: 'Anexar documento' },
                                            { icon: MessageCircle, label: 'Nota / WhatsApp' },
                                        ].map(({ icon: Icon, label }) => (
                                            <button
                                                key={label}
                                                type="button"
                                                onClick={() => onAbrirLead(parada.negociacaoId!)}
                                                className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-semibold border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40 text-gray-600 dark:text-gray-300 hover:border-primary/40 hover:text-primary transition-all text-left"
                                            >
                                                <Icon size={13} className="shrink-0" /> {label}
                                            </button>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => onAbrirLead(parada.negociacaoId!)}
                                            className="col-span-2 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-bold border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-all"
                                        >
                                            <ExternalLink size={13} /> Abrir lead completo
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <button
                        onClick={confirmar}
                        disabled={!podeConfirmar || salvando}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl text-white transition-opacity disabled:opacity-40 ${etapa === 'iniciar' ? 'bg-primary hover:opacity-90' : 'bg-emerald-500 hover:bg-emerald-600'}`}
                    >
                        <CheckCircle2 size={16} />
                        {salvando ? 'Salvando...' : etapa === 'iniciar' ? 'Fazer Check-in' : agendaProxima ? 'Concluir e agendar próxima' : 'Concluir Visita'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default VisitaModal;
